// Renders the "share your HEARTS engagement" image: the speaker's photo and
// engagement details composited onto speaker_share_template.png. Runs
// entirely client-side on a <canvas> - there is no server-side image
// generation, so this is the only place these coordinates/colors live. They
// were measured directly off the template's pixel data (a fixed 1055x1491
// PNG), not guessed - if the template art changes, these constants need
// re-measuring to match.
//
// Every text row is drawn by fully repainting its rounded box (not just
// stamping text over the template's own placeholder copy like "SPEAKER
// NAME") - painting only the text would leave mismatched placeholder glyphs
// showing through around real content of a different length/script.

export const CARD_W = 1055;
export const CARD_H = 1491;

const PHOTO = { cx: 296, cy: 902, r: 158 };

const NAME_BOX = { x: 77, y: 1017, w: 903, h: 90, r: 26 };
const QUOTE_BOX = { x: 77, y: 1124, w: 903, h: 97, r: 26 };
const TITLE_BOX = { x: 77, y: 1237, w: 519, h: 88, r: 24 };
const TIME_BOX = { x: 613, y: 1238, w: 367, h: 87, r: 24 };
const BADGE_R = 44;

const BOX_FILL = '#EEF0FE';
const NAVY = '#1A0F4B';
const SUBTLE = '#645A93';
const QUOTE_TEXT = '#3B2E82';
const ICON_PURPLE = '#958AFA';

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function paintBox(ctx, box) {
  roundRectPath(ctx, box.x, box.y, box.w, box.h, box.r);
  ctx.fillStyle = BOX_FILL;
  ctx.fill();
}

// Shrinks font-size until `text` fits maxWidth (never below minSize).
function fitFontSize(ctx, text, maxWidth, baseSize, weight, minSize = 13, italic = false) {
  let size = baseSize;
  const style = `${italic ? 'italic ' : ''}${weight} ${size}px Roboto`;
  ctx.font = style;
  while (size > minSize && ctx.measureText(text).width > maxWidth) {
    size -= 1;
    ctx.font = `${italic ? 'italic ' : ''}${weight} ${size}px Roboto`;
  }
  return size;
}

function ellipsize(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t}…`;
}

// Splits `text` into lines no wider than maxWidth at the context's current
// font. A single word wider than a line is broken, so no line can overflow.
function wrapText(ctx, text, maxWidth) {
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      continue;
    }
    if (line) {
      lines.push(line);
      line = '';
    }
    let rest = word;
    while (rest.length > 1 && ctx.measureText(rest).width > maxWidth) {
      let cut = rest.length - 1;
      while (cut > 1 && ctx.measureText(rest.slice(0, cut)).width > maxWidth) cut -= 1;
      lines.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    line = rest;
  }
  if (line) lines.push(line);
  return lines;
}

// The largest font size from baseSize down to minSize at which the wrapped text
// fits the box's width, height and line limit. Only text that cannot fit even
// at minSize is cut with an ellipsis.
function fitWrappedText(ctx, text, {
  maxWidth, maxHeight, baseSize, minSize, weight, italic = false, maxLines = 99, lineHeight = 1.2,
}) {
  const applyFont = (size) => {
    ctx.font = `${italic ? 'italic ' : ''}${weight} ${size}px Roboto`;
  };
  const fits = (size, lines) => lines.length > 0
    && lines.length <= maxLines
    && lines.length * size * lineHeight <= maxHeight;
  for (let size = baseSize; size >= minSize; size -= 1) {
    applyFont(size);
    const lines = wrapText(ctx, text, maxWidth);
    if (fits(size, lines)) return { size, lines };
  }
  applyFont(minSize);
  const lines = wrapText(ctx, text, maxWidth);
  const maxFit = Math.max(1, Math.min(maxLines, Math.floor(maxHeight / (minSize * lineHeight))));
  if (lines.length <= maxFit) return { size: minSize, lines };
  const kept = lines.slice(0, maxFit - 1);
  kept.push(ellipsize(ctx, lines.slice(maxFit - 1).join(' '), maxWidth));
  return { size: minSize, lines: kept };
}

// Draws wrapped lines as one vertically centred block. Callers set textAlign and
// textBaseline first; `x`/`w` are the block's left edge and width.
function drawWrappedLines(ctx, lines, { x, w, y, h, size, lineHeight, align = 'left' }) {
  const lineH = size * lineHeight;
  const firstY = y + (h - lineH * lines.length) / 2 + lineH / 2;
  const startX = align === 'center' ? x + w / 2 : x;
  lines.forEach((line, i) => ctx.fillText(line, startX, firstY + i * lineH));
}

export function initialsFrom(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';
}

export function loadImage(src, crossOrigin) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load image: ${src}`));
    img.src = src;
  });
}

// Person photos come from two worlds (see mediaUrl.js): our own /uploads/**
// media, served from the app's own origin, and absolute external URLs from AI
// research / social profiles. Only a cross-origin image needs a CORS opt-in to
// stay drawable; setting crossOrigin on a same-origin image can make a server
// that does not answer the preflight/CORS headers drop the request entirely, so
// our own photos (a relative path, or an absolute URL on the app origin - the
// production case, where API and frontend share a host) are loaded plainly.
function isCrossOrigin(src) {
  if (!src || /^(data|blob):/i.test(src)) return false;
  try {
    return new URL(src, window.location.href).origin !== window.location.origin;
  } catch (e) {
    return false;
  }
}

function drawPhoto(ctx, img) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(PHOTO.cx, PHOTO.cy, PHOTO.r, 0, Math.PI * 2);
  ctx.clip();
  const scale = Math.max((PHOTO.r * 2) / img.width, (PHOTO.r * 2) / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, PHOTO.cx - dw / 2, PHOTO.cy - dh / 2, dw, dh);
  ctx.restore();
}

function drawInitialsAvatar(ctx, name) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(PHOTO.cx, PHOTO.cy, PHOTO.r, 0, Math.PI * 2);
  ctx.fillStyle = '#DCE3FB';
  ctx.fill();
  ctx.fillStyle = '#6B7FD7';
  ctx.font = `700 ${Math.round(PHOTO.r * 0.8)}px Roboto`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initialsFrom(name), PHOTO.cx, PHOTO.cy + PHOTO.r * 0.06);
  ctx.restore();
}

function drawIconBadge(ctx, cx, cy, kind) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, BADGE_R, 0, Math.PI * 2);
  ctx.fillStyle = ICON_PURPLE;
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.fillStyle = '#fff';
  ctx.lineWidth = Math.max(2.5, BADGE_R * 0.09);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (kind === 'title') {
    const w = BADGE_R * 0.7, h = BADGE_R * 0.9, x0 = cx - w / 2, y0 = cy - h / 2, rr = 4;
    roundRectPath(ctx, x0, y0, w, h, rr);
    ctx.stroke();
    ctx.beginPath();
    [0.32, 0.52, 0.72].forEach((f) => { ctx.moveTo(x0 + w * 0.18, y0 + h * f); ctx.lineTo(x0 + w * 0.82, y0 + h * f); });
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.arc(cx, cy, BADGE_R * 0.55, 0, Math.PI * 2);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx, cy - BADGE_R * 0.32);
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + BADGE_R * 0.22, cy + BADGE_R * 0.06);
    ctx.stroke();
  }
  ctx.restore();
}

function drawQuoteGlyph(ctx, x, y, flip) {
  ctx.save();
  ctx.fillStyle = ICON_PURPLE;
  ctx.font = 'italic 700 64px Georgia, serif';
  ctx.textBaseline = 'middle';
  ctx.textAlign = flip ? 'right' : 'left';
  ctx.fillText(flip ? '”' : '“', x, y);
  ctx.restore();
}

// name: display name. subtitle: "Institution / City, Country" line.
// quote: optional short personal line - the box is left blank (repainted,
// no placeholder) when omitted rather than showing template sample copy.
// talkTitle/talkTime: the engagement's talk (or role/session) and its slot.
export async function renderShareCard(canvas, templateSrc, { photoUrl, name, subtitle, quote, talkTitle, talkTime }) {
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');

  if (document.fonts && document.fonts.ready) {
    try { await document.fonts.ready; } catch (e) { /* proceed with fallback font */ }
  }

  const template = await loadImage(templateSrc);
  ctx.drawImage(template, 0, 0, CARD_W, CARD_H);

  // A cross-origin photo is requested with crossOrigin='anonymous' so the
  // canvas stays untainted and the PNG can be exported; a same-origin photo
  // needs no opt-in at all. If the cross-origin load fails (a third-party host
  // with no CORS headers) we fall back to the initials avatar rather than
  // retrying the load plainly: a plain cross-origin image taints the canvas,
  // which makes toBlob throw at download time - no image at all beats a broken
  // download. drawPhoto itself is guarded for the same reason (an image that
  // decodes but is somehow tainted must not take the export down with it).
  let photoImg = null;
  if (photoUrl) {
    try {
      photoImg = isCrossOrigin(photoUrl)
        ? await loadImage(photoUrl, 'anonymous')
        : await loadImage(photoUrl);
    } catch (e) { photoImg = null; }
  }
  let photoDrawn = false;
  if (photoImg) {
    try {
      drawPhoto(ctx, photoImg);
      photoDrawn = true;
    } catch (e) { drawInitialsAvatar(ctx, name); }
  } else {
    drawInitialsAvatar(ctx, name);
  }

  // Name / institution box
  paintBox(ctx, NAME_BOX);
  const padX = 40;
  const innerW = NAME_BOX.w - padX * 2;
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = NAVY;
  const nameSize = fitFontSize(ctx, name || '', innerW, 34, 700, 18);
  ctx.font = `700 ${nameSize}px Roboto`;
  ctx.fillText(ellipsize(ctx, name || '', innerW), NAME_BOX.x + padX, NAME_BOX.y + 45);
  if (subtitle) {
    // Same rule as the name above: shrink to a readable minimum, truncate only
    // past it. A subtitle that already fits keeps its 20px.
    const subSize = fitFontSize(ctx, subtitle, innerW, 20, 400, 14);
    ctx.font = `400 ${subSize}px Roboto`;
    ctx.fillStyle = SUBTLE;
    ctx.fillText(ellipsize(ctx, subtitle, innerW), NAME_BOX.x + padX, NAME_BOX.y + 75);
  }

  // Quote box. The note is no longer capped at 90 characters: it wraps and the
  // font shrinks until the block fits the box, so only a note that cannot fit
  // even at the minimum size is cut.
  paintBox(ctx, QUOTE_BOX);
  if (quote && quote.trim()) {
    const qy = QUOTE_BOX.y + QUOTE_BOX.h / 2;
    drawQuoteGlyph(ctx, QUOTE_BOX.x + 38, qy, false);
    drawQuoteGlyph(ctx, QUOTE_BOX.x + QUOTE_BOX.w - 38, qy, true);
    const qInnerW = QUOTE_BOX.w - 190;
    const qFit = fitWrappedText(ctx, quote.trim(), {
      maxWidth: qInnerW, maxHeight: QUOTE_BOX.h - 8, baseSize: 24, minSize: 12,
      weight: 500, italic: true, maxLines: 6, lineHeight: 1.18,
    });
    ctx.fillStyle = QUOTE_TEXT;
    ctx.font = `italic 500 ${qFit.size}px Roboto`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    drawWrappedLines(ctx, qFit.lines, {
      x: QUOTE_BOX.x, w: QUOTE_BOX.w, y: QUOTE_BOX.y, h: QUOTE_BOX.h,
      size: qFit.size, lineHeight: 1.18, align: 'center',
    });
    ctx.textAlign = 'left';
  }

  // Talk title box. A title that fits one line shrinks (down to 12px) and stays
  // on one line, as before; only a title too long even for that wraps onto a
  // second line and keeps shrinking. Ellipsis remains for titles too long even
  // at the two-line minimum.
  paintBox(ctx, TITLE_BOX);
  {
    const bcx = TITLE_BOX.x + 68, bcy = TITLE_BOX.y + TITLE_BOX.h / 2;
    drawIconBadge(ctx, bcx, bcy, 'title');
    const tx = bcx + BADGE_R + 26;
    const tw = TITLE_BOX.x + TITLE_BOX.w - 24 - tx;
    const title = talkTitle || '';
    const singleSize = fitFontSize(ctx, title, tw, 24, 600, 12);
    ctx.font = `600 ${singleSize}px Roboto`;
    ctx.fillStyle = NAVY;
    ctx.textBaseline = 'middle';
    if (ctx.measureText(title).width <= tw) {
      ctx.fillText(title, tx, bcy + 1);
    } else {
      const tFit = fitWrappedText(ctx, title, {
        maxWidth: tw, maxHeight: TITLE_BOX.h - 8, baseSize: 24, minSize: 11,
        weight: 600, maxLines: 2, lineHeight: 1.15,
      });
      ctx.font = `600 ${tFit.size}px Roboto`;
      drawWrappedLines(ctx, tFit.lines, {
        x: tx, w: tw, y: TITLE_BOX.y, h: TITLE_BOX.h,
        size: tFit.size, lineHeight: 1.15,
      });
    }
  }

  // Talk time box
  paintBox(ctx, TIME_BOX);
  {
    const bcx = TIME_BOX.x + 68, bcy = TIME_BOX.y + TIME_BOX.h / 2;
    drawIconBadge(ctx, bcx, bcy, 'time');
    const tx = bcx + BADGE_R + 26;
    const tw = TIME_BOX.x + TIME_BOX.w - 24 - tx;
    ctx.fillStyle = NAVY;
    ctx.textBaseline = 'middle';
    const size = fitFontSize(ctx, talkTime || '', tw, 22, 600, 14);
    ctx.font = `600 ${size}px Roboto`;
    ctx.fillText(ellipsize(ctx, talkTime || '', tw), tx, bcy + 1);
  }

  // `photoDrawn` is false when no photo was supplied or when the photo could not be loaded or
  // drawn (a host without CORS, a dead URL, a tainted image); the card then shows the initials.
  // The modal uses this to tell the speaker why and what to do, without treating it as a failure.
  return { canvas, photoDrawn };
}
